import { FC, useState } from "react";
import { Grid2, Chip, Tooltip } from "@mui/material";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { ReportEntry } from "../../../@types/ReportEntry";
import { Status } from "../../../enums/Status";
import { statusBackgroundColor, statusTextColor } from "../../../enums/Status";
import i18next from "i18next";
import Modal from "../../atoms/Modal";
import {
  canViewRefusedReason,
  canViewApprovedReasonPerFinancial,
  canViewApprovedReasonPerManager,
  hasComments,
} from "../../../utils/reportStatusUtils";

interface StatusChipProps {
  data: ReportEntry;
}

export const StatusChip: FC<StatusChipProps> = ({ data }) => {
  const [viewDetailsModal, setViewDetailsModal] = useState<number | null>(null);

  return (
    <>
      <Grid2 container direction="column" spacing={1} sx={{ gap: "4px" }}>
        <Chip
          onClick={(e) => {
            e.stopPropagation();
            hasComments(data) && setViewDetailsModal(data.id);
          }}
          label={
            <span key={`status-chip-${data.id}`} className="flex flex-row items-center justify-between w-full gap-1">
              {hasComments(data) && <div className="flex-1" />}
              <span className="flex-1 text-center">
                {data.reportEntryStatus === "ON_APPROVAL" && data.approvedByOwner
                  ? i18next.t(`status.ON_APPROVAL_PENDING_FINANCIAL`)
                  : data.reportEntryStatus === "ON_APPROVAL" && data.approvedByFinancial
                  ? i18next.t(`status.ON_APPROVAL_PENDING_OWNER`)
                  : i18next.t(`status.${data.reportEntryStatus}`)}
              </span>
              {hasComments(data) && (
                <div className="flex-1 flex justify-end">
                  <Tooltip title={data.reportEntryStatus === Status.REFUSED ? data.refusedReason : (data.financialApprovedReason || data.ownerApprovedReason)}>
                    <InformationCircleIcon
                      onClick={(e) => {
                        e.stopPropagation();
                        setViewDetailsModal(data.id);
                      }}
                      stroke={statusTextColor(data.reportEntryStatus)}
                      className="w-5 h-5 text-blue-500 cursor-pointer"
                    />
                  </Tooltip>
                </div>
              )}
            </span>
          }
          sx={{
            maxWidth: "180px",
            backgroundColor: statusBackgroundColor(data.reportEntryStatus),
            color: statusTextColor(data.reportEntryStatus),
            fontSize: "0.75rem",
            height: "24px",
            cursor: hasComments(data) ? "pointer" : "default",
            "&:hover": {
              backgroundColor: statusBackgroundColor(data.reportEntryStatus),
            },
            "& .MuiChip-label": {
              padding: "0 8px",
              width: "100%",
            },
          }}
        />
        {data.clientRefund && (
          <Chip
            label="Reembolso pelo cliente"
            size="small"
            sx={{
              maxWidth: "180px",
              backgroundColor: "#E8F5E9",
              color: "#2E7D32",
              fontSize: "0.75rem",
              height: "24px",
              "& .MuiChip-label": {
                padding: "0 8px",
              },
            }}
          />
        )}
      </Grid2>

      <Modal
        title="Detalhes do status"
        isOpen={viewDetailsModal === data.id}
        setIsOpen={() => setViewDetailsModal(null)}
        widthPanel={"w-1/6 tablet-max:w-[95%]"}
        body={
          <div className="flex flex-col gap-2 mt-2">
            {canViewApprovedReasonPerFinancial(data) && (
              <>
                <label className="text-sm font-light">Financeiro:</label>
                <textarea
                  className="w-full bg-gray-50 border border-gray-300 text-gray-500 text-sm rounded-md block p-1 dark:bg-white dark:border-gray-400 dark:placeholder-gray-400 dark:text-gray-600 outline-none"
                  name="refuseReason"
                  rows={5}
                  value={data.financialApprovedReason}
                  readOnly
                />
              </>
            )}

            {canViewApprovedReasonPerManager(data) && (
              <>
                <label className="text-sm font-light mt-4">Gestor:</label>
                <textarea
                  className="w-full bg-gray-50 border border-gray-300 text-gray-500 text-sm rounded-md block p-1 dark:bg-white dark:border-gray-400 dark:placeholder-gray-400 dark:text-gray-600 outline-none"
                  name="refuseReason"
                  rows={5}
                  value={data.ownerApprovedReason}
                  readOnly
                />
              </>
            )}

            {canViewRefusedReason(data) && (
              <>
                <label className="text-sm font-light">Motivo de rejeição:</label>
                <textarea
                  className="w-full bg-gray-50 border border-gray-300 text-gray-500 text-sm rounded-md block p-1 dark:bg-white dark:border-gray-400 dark:placeholder-gray-400 dark:text-gray-600 outline-none"
                  name="refuseReason"
                  rows={5}
                  value={data.refusedReason}
                  readOnly
                />
              </>
            )}
          </div>
        }
        bottom={
          <div className="flex flex-row justify-around mt-4">
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-transparent bg-green-400 px-4 py-2 text-sm font-medium text-white hover:bg-vcolor focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              onClick={() => setViewDetailsModal(null)}
            >
              Ok
            </button>
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-transparent bg-red-400 px-4 py-2 text-sm font-medium text-white hover:bg-vcolor focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              onClick={() => setViewDetailsModal(null)}
            >
              Voltar
            </button>
          </div>
        }
      />
    </>
  );
};

export default StatusChip;

